import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';
import { createProject } from '../../../features/sessions/projectsManagement/sessionManageProjects';
import LoadingModal from '../../modal/loadingModal/LoadingModal';
import { toast } from 'react-toastify';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, Radio, RadioGroup, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

const BG_DIALOG      = '#1c1c24';
const BG_INPUT       = '#24242e';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

const inputSx = {
    '& .MuiOutlinedInput-root': {
        color: TEXT_PRIMARY,
        bgcolor: BG_INPUT,
        '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },

        '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
            WebkitBoxShadow: `0 0 0 100px #1c1c24 inset`,
            WebkitTextFillColor: '#f4f4f6',
            caretColor: '#f4f4f6',
        },
        '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' },
    },
    '& .MuiInputLabel-root': { color: TEXT_SECONDARY },
    '& .MuiInputLabel-root.Mui-focused': { color: '#f4f4f6', fontWeight: 700 },
};

export default function CreateProjectForm() {
    const currentUser = useSelector((state: RootState) => state.session.currentUser);
    const authHeaders = useSelector((state: RootState) => state.session.authHeaders);
    const dispatch    = useDispatch<AppDispatch>();

    const [open, setOpen]                       = useState(false);
    const [titleForm, setTitleForm]             = useState('');
    const [descriptionForm, setDescriptionForm] = useState('');
    const [privacityFormBody, setPrivacityFormBody] = useState(0);

    async function handleCreateProject(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        const postBody = { title: titleForm, description: descriptionForm, privacity: privacityFormBody };
        if (authHeaders && currentUser && postBody) {
            const response = await dispatch(createProject({ authHeaders, currentUser, postBody }));
            if (response.payload.errors && response.payload.errors.length > 0) {
                response.payload.errors.map((item: string) => { toast.error(item); });
            } else {
                setOpen(false);
                setTitleForm('');
                setDescriptionForm('');
                setPrivacityFormBody(0);
            }
        }
    }

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Button
                    variant='contained'
                    startIcon={<AddIcon />}
                    onClick={() => setOpen(true)}
                    sx={{
                        bgcolor: BRAND_RED,
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': { bgcolor: '#a93226' },
                    }}
                >
                    Create Project
                </Button>
            </Box>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth='sm'
                fullWidth
                PaperProps={{ sx: { bgcolor: BG_DIALOG, color: TEXT_PRIMARY, border: BORDER_SUBTLE } }}
            >
                <form onSubmit={handleCreateProject}>
                    <DialogTitle sx={{ fontWeight: 700, color: TEXT_PRIMARY }}>
                        Create Project
                    </DialogTitle>
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                        <TextField
                            label='Title'
                            size='small'
                            fullWidth
                            value={titleForm}
                            inputProps={{ maxLength: 71 }}
                            onChange={(e) => setTitleForm(e.target.value)}
                            sx={inputSx}
                        />
                        <TextField
                            label='Description'
                            size='small'
                            fullWidth
                            multiline
                            rows={3}
                            value={descriptionForm}
                            onChange={(e) => setDescriptionForm(e.target.value)}
                            sx={inputSx}
                        />
                        <Box>
                            <Typography variant='caption' color={TEXT_SECONDARY} sx={{ mb: 0.5, display: 'block' }}>
                                Privacy
                            </Typography>
                            <RadioGroup
                                row
                                value={privacityFormBody === 0 ? 'public' : 'private'}
                                onChange={(e) => setPrivacityFormBody(e.target.value === 'public' ? 0 : 1)}
                            >
                                <FormControlLabel
                                    value='public'
                                    control={
                                        <Radio size='small' sx={{ color: TEXT_SECONDARY, '&.Mui-checked': { color: BRAND_RED } }} />
                                    }
                                    label={<Typography variant='body2' color={TEXT_PRIMARY}>Public</Typography>}
                                />
                                <FormControlLabel
                                    value='private'
                                    control={
                                        <Radio size='small' sx={{ color: TEXT_SECONDARY, '&.Mui-checked': { color: BRAND_RED } }} />
                                    }
                                    label={<Typography variant='body2' color={TEXT_PRIMARY}>Private</Typography>}
                                />
                            </RadioGroup>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                        <Button
                            onClick={() => setOpen(false)}
                            sx={{ color: TEXT_SECONDARY, textTransform: 'none' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type='submit'
                            variant='contained'
                            sx={{ bgcolor: BRAND_RED, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#a93226' } }}
                        >
                            Create
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </>
    );
}
